import { provideHttpClient, withInterceptors, withXhr } from '@angular/common/http';
import { ApplicationConfig, inject, provideAppInitializer, provideBrowserGlobalErrorListeners } from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
  NavigationError,
  provideRouter,
  RedirectCommand,
  Router,
  withNavigationErrorHandler
} from '@angular/router';

import { routes } from './app.routes';
import { provideStaleBundleRecovery } from '@core/providers/stale-bundle-recovery.provider';
import { authInterceptor } from '@core/interceptors/auth.interceptor';
import { loadingInterceptor } from '@core/interceptors/loading.interceptor';
import { AmcfTabCoordinatorService } from '@core/amcf-tab-coordinator';
import { ToastService } from '@core/services';
import { environment } from '@environments/environment';
import { firstValueFrom } from 'rxjs';
import { handleStaleBundleError, isStaleBundleError } from '@core/utils/bundle-update.utils';
import { resolveUnknownErrorMessage } from '@core/utils/validation.utils';

export const appConfig: ApplicationConfig = {
  providers: [
    provideAppInitializer(() => {
      if (!environment.AMCF_TAB_COORDINATOR_ENABLED) {
        return;
      }
      const coordinator = inject(AmcfTabCoordinatorService);
      return firstValueFrom(coordinator.claimActiveTab());
    }),
    provideBrowserGlobalErrorListeners(),
    provideStaleBundleRecovery(),
    provideAnimationsAsync(),
    provideRouter(
      routes,
      // Handler globale errori di navigazione (lazy load, guard, resolver).
      // Bundle obsoleto → reload; altri errori → toast + redirect /login; vedi docs/routing.md.
      withNavigationErrorHandler((error: NavigationError) => {
        const router = inject(Router);
        const toastService = inject(ToastService);

        if (isStaleBundleError(error.error)) {
          handleStaleBundleError(toastService);
          return;
        }

        const targetUrl = error.target?.url ?? error.url;
        if (targetUrl.startsWith('/login')) {
          return;
        }

        const fallback =
          'Si è verificato un errore durante la navigazione. Effettua di nuovo l\'accesso.';
        toastService.presentaToastErrore(resolveUnknownErrorMessage(error.error, fallback));

        return new RedirectCommand(router.parseUrl('/login'));
      })
    ),
    provideHttpClient(
      withXhr(),
      withInterceptors([authInterceptor, loadingInterceptor])
    )
  ]
};
